import React, { useState, useEffect } from 'react';
import cn from 'classnames';
import noop from 'lodash/noop';
import { getDigestNotificationSummary } from '../../helpers';
import { DAY_OF_WEEK_LIST, HOURS_OPTIONS } from '../common/constants';
import Spinner from '@dealroadshow/uikit-loader/Spinner';
import IndeterminateCheckbox from '../common/IndeterminateCheckbox';
import SelectField from '../common/SelectField';
import { IDigestNotificationSettingsPayload } from '../../domain/vo/DigestEmailAlerts';
import { ITimezone } from '@framework/timezone/vo/Timezone';
import styles from './digestAlerts.module.scss';

interface IProps<T> {
  name?: string,
  initialValues: T,
  isFetching?: boolean,
  isDisabled?: boolean,
  timezone?: ITimezone,
  onChange: (value: T) => void,
  isNoneCheckboxVisible?: boolean,
}

const DigestAlerts = <T extends IDigestNotificationSettingsPayload = IDigestNotificationSettingsPayload>({
  name = 'dataroomDigestAlertsForm',
  initialValues,
  isFetching = false,
  isDisabled,
  timezone,
  onChange = noop,
  isNoneCheckboxVisible = true,
}: IProps<T>) => {
  const [digestAlertsState, setDigestAlertsState] = useState<T>(initialValues);

  useEffect(() => {
    onChange(digestAlertsState);
  }, [digestAlertsState]);

  useEffect(() => {
    isDisabled
      ? setDigestAlertsState({
        ...initialValues,
        isActiveDigestAlerts: true,
        isDigestDaily: false,
      })
      : setDigestAlertsState(initialValues);
  }, [initialValues, isDisabled]);

  const handleCheckboxFieldChange = (
    field: keyof T & `is${ string }`,
    value: boolean,
  ) => {
    const state: T = {
      ...digestAlertsState,
      [field]: value,
    };

    if (field === 'isActiveDigestAlerts' && value === true) {
      state.isActiveDigestAlerts = true;
      state.isDigestDaily = false;
      state.isDigestWeekly = false;
    } else if (value === true) {
      state.isActiveDigestAlerts = false;
    }

    if (
      state.isActiveDigestAlerts === false &&
      state.isDigestDaily === false &&
      state.isDigestWeekly === false
    ) {
      state.isActiveDigestAlerts = true;
    }

    setDigestAlertsState(state);
  };

  const handleSelectFieldChange = (
    name: keyof Omit<T, keyof T & `is${ string }`>,
    value: T[keyof Omit<T, keyof T & `is${ string }`>],
  ) => {
    if (!value) return;

    setDigestAlertsState({
      ...digestAlertsState,
      [name]: value,
    });
  };

  return (
    <div data-test={ name }>
      { isNoneCheckboxVisible && (
        <div className={ styles.itemAlerts }>
          <IndeterminateCheckbox
            name="isActiveDigestAlerts"
            dataTest="noneCheckbox"
            label="None"
            disabled={ isDisabled }
            initialValue={ initialValues.isActiveDigestAlerts }
            value={ digestAlertsState.isActiveDigestAlerts }
            onChange={ (value) => handleCheckboxFieldChange('isActiveDigestAlerts' as keyof T & `is${ string }`, value) }
            className={ styles.checkbox }
          />
        </div>
      ) }
      <div className={ styles.itemAlerts }>
        <IndeterminateCheckbox
          name="isDigestDaily"
          dataTest="dailyCheckbox"
          label="Daily summary"
          disabled={ isDisabled }
          initialValue={ initialValues.isDigestDaily }
          value={ digestAlertsState.isDigestDaily }
          onChange={ (value) => handleCheckboxFieldChange('isDigestDaily' as keyof T & `is${ string }`, value) }
          className={ styles.checkbox }
        />
        <div className={ cn(styles.inlineBlock, styles.wrpSelect) }>
          <SelectField<T>
            name="digestDailyTime"
            dataTest="digestDailyTime_digestAlertSelect"
            selectClassName={ styles.timeSelect }
            options={ HOURS_OPTIONS }
            disabled={ !digestAlertsState.isDigestDaily || !!isDisabled }
            value={ digestAlertsState.digestDailyTime }
            onChange={ handleSelectFieldChange as any }
          />
        </div>
      </div>
      <div className={ styles.itemAlerts }>
        <IndeterminateCheckbox
          name="isDigestWeekly"
          dataTest="weeklyCheckbox"
          label="Weekly summary"
          disabled={ isDisabled }
          initialValue={ initialValues.isDigestWeekly }
          value={ digestAlertsState.isDigestWeekly }
          onChange={ (value) => handleCheckboxFieldChange('isDigestWeekly' as keyof T & `is${ string }`, value) }
          className={ styles.checkbox }
        />
        <div className={ cn(styles.inlineBlock, styles.wrpSelect) }>
          <SelectField<T>
            name="digestWeeklyTime"
            dataTest="digestWeeklyTime_digestAlertSelect"
            selectClassName={ styles.timeSelect }
            options={ HOURS_OPTIONS }
            disabled={ !digestAlertsState.isDigestWeekly || !!isDisabled }
            value={ digestAlertsState.digestWeeklyTime }
            onChange={ handleSelectFieldChange as any }
          />
        </div>
        <div className={ cn(styles.inlineBlock, styles.dayWeek) }>
          <SelectField<T>
            name="digestWeeklyDay"
            dataTest="digestWeeklyDay_digestAlertSelect"
            selectClassName={ styles.selectDaysOfWeek }
            options={ DAY_OF_WEEK_LIST }
            disabled={ !digestAlertsState.isDigestWeekly || !!isDisabled }
            value={ digestAlertsState.digestWeeklyDay }
            onChange={ handleSelectFieldChange as any }
          />
        </div>
      </div>
      { timezone && (
        <div className={ styles.footer }>
          <span className={ styles.strong }>
            Summary:
          </span> { getDigestNotificationSummary(digestAlertsState, timezone.abbr) }
        </div>
      ) }
      <Spinner
        overlay
        isVisible={ isFetching }
      />
    </div>
  );
};

export default DigestAlerts;
